import { AiChatInputRequestStatus } from '@amitable/shared/ai-chat-decisions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { normalizeAiChatMessage } from './ai-chat-message'
import { useAiChatDraft } from './use-ai-chat-draft'
import { useAiChatSubmitPrompt } from './ai-chat-live-actions'
import { useAiChatHistory } from './use-ai-chat-history'
import {
  submitDraftWithFiles,
  useAiChatLiveFiles,
} from './files/use-ai-chat-live-files'
import { useAiChatConversationActions } from './ai-chat-conversation-actions'
import { getAiChatScopeVariables, isAiChatUnscoped } from './ai-chat-scope'
import type { useAiChatFiles } from './files/use-ai-chat-files'
import type { AiChatNavigationContext } from '@amitable/shared/ai-chat-navigation'
import type { AiChatScopeInput } from './ai-chat-scope'
import type { SentMessagesBySessionId } from './ai-chat-live-actions'
import type { LiveAiChatMessage } from './ai-chat-live-content'
import { useStartDocumentCollectionAiChatMutation } from '@/graphql/mutations/startDocumentCollectionAiChatMutation.gql.generated'
import { useStartWorkspaceAiChatMutation } from '@/graphql/mutations/startWorkspaceAiChatMutation.gql.generated'
import { useAiChatSessionsQuery } from '@/graphql/queries/aiChatSessionsQuery.gql.generated'
import { useAiChatMessagesQuery } from '@/graphql/queries/aiChatMessagesQuery.gql.generated'

type LiveStateInput = AiChatScopeInput & {
  open: boolean
  navigationContext?: AiChatNavigationContext
}

export function mergeAiChatMessages(
  queryMessages: Array<LiveAiChatMessage>,
  sentMessages: Array<LiveAiChatMessage>,
) {
  const byId = new Map<string, LiveAiChatMessage>()
  for (const message of [...queryMessages, ...sentMessages]) {
    const existing = byId.get(message.id)
    const inputRequest = existing?.inputRequest
    byId.set(message.id, {
      ...message,
      ...(inputRequest &&
      inputRequest.status !== AiChatInputRequestStatus.PENDING &&
      message.inputRequest?.status === AiChatInputRequestStatus.PENDING
        ? { inputRequest }
        : {}),
    })
  }

  return [...byId.values()]
}

function useAiChatLocalState() {
  // undefined follows the saved active session; null selects an unsaved chat.
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>()
  const [titles, setTitles] = useState<Partial<Record<string, string>>>({})
  const [historyOpen, setHistoryOpen] = useState(false)
  const [followRequest, setFollowRequest] = useState(0)
  const [sentMessagesBySessionId, setSentMessagesBySessionId] =
    useState<SentMessagesBySessionId>({})
  const [submitting, setSubmitting] = useState(false)
  const [responding, setResponding] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const streamAbortControllerRef = useRef<AbortController | null>(null)
  useEffect(() => {
    const controllerRef = streamAbortControllerRef

    return () => controllerRef.current?.abort()
  }, [])

  return {
    selectedSessionId,
    setSelectedSessionId,
    titles,
    setTitle: (id: string, title: string) =>
      setTitles((current) => ({ ...current, [id]: title })),
    historyOpen,
    setHistoryOpen,
    followRequest,
    follow: () => setFollowRequest((current) => current + 1),
    sentMessagesBySessionId,
    setSentMessagesBySessionId,
    submitting,
    setSubmitting,
    responding,
    setResponding,
    submitError,
    setSubmitError,
    streamAbortControllerRef,
  }
}

function useAiChatQueries(
  input: LiveStateInput,
  selectedSessionId: string | null | undefined,
) {
  const sessionsQuery = useAiChatSessionsQuery({
    skip: !input.open || isAiChatUnscoped(input),
    fetchPolicy: 'network-only',
    variables: getAiChatScopeVariables(input),
  })
  const activeSessionId =
    selectedSessionId === undefined
      ? sessionsQuery.data?.aiChatSessions[0]?.id
      : (selectedSessionId ?? undefined)
  const messagesQuery = useAiChatMessagesQuery({
    skip: !input.open || !activeSessionId,
    fetchPolicy: 'network-only',
    variables: { sessionId: activeSessionId ?? '' },
  })

  return { sessionsQuery, messagesQuery, activeSessionId }
}

function useAiChatMutations() {
  const [startWorkspaceChat, workspaceStartState] =
    useStartWorkspaceAiChatMutation()
  const [startDocumentCollectionChat, collectionStartState] =
    useStartDocumentCollectionAiChatMutation()

  return {
    startWorkspaceChat,
    workspaceStartState,
    startDocumentCollectionChat,
    collectionStartState,
  }
}

function useAiChatMessages(
  queries: ReturnType<typeof useAiChatQueries>,
  sent: SentMessagesBySessionId,
) {
  const { activeSessionId, messagesQuery } = queries

  return useMemo(
    () =>
      activeSessionId
        ? mergeAiChatMessages(
            (messagesQuery.data?.aiChatMessages ?? [])
              .map(normalizeAiChatMessage)
              .filter((message) => message.sessionId === activeSessionId),
            sent[activeSessionId] ?? [],
          )
        : [],
    [activeSessionId, messagesQuery.data, sent],
  )
}

function getAiChatStatus(
  input: LiveStateInput,
  queries: ReturnType<typeof useAiChatQueries>,
  mutations: ReturnType<typeof useAiChatMutations>,
  submitting: boolean,
) {
  const unscoped = isAiChatUnscoped(input)
  const loading =
    queries.sessionsQuery.loading ||
    (Boolean(queries.activeSessionId) && queries.messagesQuery.loading) ||
    mutations.workspaceStartState.loading ||
    mutations.collectionStartState.loading

  return {
    loading,
    blocked: unscoped || loading || submitting,
    inputDisabled: unscoped || (loading && !submitting),
  }
}

function getAiChatError(
  local: ReturnType<typeof useAiChatLocalState>,
  queries: ReturnType<typeof useAiChatQueries>,
  mutations: ReturnType<typeof useAiChatMutations>,
) {
  return [
    local.submitError,
    queries.sessionsQuery.error?.message,
    queries.activeSessionId ? queries.messagesQuery.error?.message : undefined,
    mutations.workspaceStartState.error?.message,
    mutations.collectionStartState.error?.message,
  ].find(Boolean)
}

export function useAiChatLiveState(input: LiveStateInput) {
  const local = useAiChatLocalState()
  const queries = useAiChatQueries(input, local.selectedSessionId)
  const { activeSessionId } = queries
  const draft = useAiChatDraft(activeSessionId)
  const history = useAiChatHistory({
    ...input,
    open: input.open && local.historyOpen,
  })
  const mutations = useAiChatMutations()
  const files = useAiChatLiveFiles({
    input,
    activeSessionId,
    mutations,
    draft,
    selectSession: local.setSelectedSessionId,
  })
  const messages = useAiChatMessages(queries, local.sentMessagesBySessionId)
  const status = getAiChatStatus(input, queries, mutations, local.submitting)
  const actions = useAiChatConversationActions({
    activeSessionId,
    blocked: status.blocked,
    setSelectedSessionId: local.setSelectedSessionId,
    setTitle: local.setTitle,
    setError: local.setSubmitError,
    setHistoryOpen: local.setHistoryOpen,
    refetch: queries.sessionsQuery.refetch,
  })
  const submitPrompt = useAiChatSubmission({
    input,
    local,
    queries,
    mutations,
    draft,
    files,
    messages,
    transitioning: actions.transitioning,
  })
  const sessionTitle = getSelectedTitle({
    queries,
    local,
    history,
    messages,
    loading: status.loading,
  })
  const blocked = status.blocked || actions.transitioning
  const openHistory = () => {
    if (blocked) return
    history.reset()
    local.setHistoryOpen(true)
  }

  return {
    ...actions,
    activeSessionId,
    sessionTitle,
    blocked,
    responding: local.responding,
    submitting: local.submitting,
    loading: status.loading,
    messages,
    history,
    historyOpen: local.historyOpen,
    openHistory,
    closeHistory: () => local.setHistoryOpen(false),
    draft: draft.draft,
    setDraft: draft.setDraft,
    inputDisabled: status.inputDisabled || actions.transitioning,
    files,
    submitDraft: () => submitDraftWithFiles({ draft, files, submitPrompt }),
    submitPrompt,
    answerInputRequest: submitPrompt.answerInputRequest,
    cancelInputRequest: submitPrompt.cancelInputRequest,
    followRequest: local.followRequest,
    errorMessage: getAiChatError(local, queries, mutations),
  }
}

function useAiChatSubmission({
  input,
  local,
  queries,
  mutations,
  draft,
  files,
  messages,
  transitioning,
}: {
  input: LiveStateInput
  local: ReturnType<typeof useAiChatLocalState>
  queries: ReturnType<typeof useAiChatQueries>
  mutations: ReturnType<typeof useAiChatMutations>
  draft: ReturnType<typeof useAiChatDraft>
  files: ReturnType<typeof useAiChatFiles>
  messages: Array<LiveAiChatMessage>
  transitioning: boolean
}) {
  return useAiChatSubmitPrompt({
    messages,
    messagesRefetch: async (sessionId) => {
      const refreshed = await queries.messagesQuery.refetch({ sessionId })

      return {
        data: refreshed.data
          ? {
              aiChatMessages: refreshed.data.aiChatMessages.map(
                normalizeAiChatMessage,
              ),
            }
          : undefined,
      }
    },
    ...input,
    ...mutations,
    ...local,
    activeSessionId: queries.activeSessionId,
    blocked:
      getAiChatStatus(input, queries, mutations, local.submitting).blocked ||
      transitioning,
    sessionsRefetch: queries.sessionsQuery.refetch,
    setCreatedSessionId: (sessionId) => {
      draft.moveToSession(sessionId)
      files.moveToSession(sessionId)
      local.setSelectedSessionId(sessionId)
    },
    onUserMessageAccepted: (sessionId, message) => {
      local.follow()
      if (message && !messages.length)
        local.setTitle(sessionId, message.slice(0, 160))
    },
    onSessionUpdated: local.setTitle,
  })
}

function getSelectedTitle({
  queries,
  local,
  history,
  messages,
  loading,
}: {
  queries: ReturnType<typeof useAiChatQueries>
  local: ReturnType<typeof useAiChatLocalState>
  history: ReturnType<typeof useAiChatHistory>
  messages: Array<LiveAiChatMessage>
  loading: boolean
}) {
  const { activeSessionId } = queries
  const selectedSession =
    queries.sessionsQuery.data?.aiChatSessions.find(
      (session) => session.id === activeSessionId,
    ) ?? history.sessions.find((session) => session.id === activeSessionId)

  return activeSessionId && (messages.length || loading)
    ? (local.titles[activeSessionId] ?? selectedSession?.title)
    : undefined
}
